import { AlertTriangle, Link2, Link2Off } from "lucide-react";
import type { CitationAudit } from "../../domain/types";
import { cn } from "../common/cn";

interface CitationAuditBadgeProps {
  audit: CitationAudit;
}

/**
 * İş Kolu E2 — surfaces `citation_auditor.audit_citation_adherence()`'s
 * result in the transcript. Before this component existed, the audit
 * ran on every turn with research sources but the result only ever
 * reached a structured server log line (`turn.citation_adherence`) —
 * invisible without SSH + journalctl access.
 *
 * Renders when there were sources to audit, OR when the deterministic
 * checks (ungrounded numeric claim, fabricated named authority) fired
 * regardless of source count — a turn with ZERO real sources that
 * still invents "Gartner'a göre…" has something very meaningful to
 * report (see `fabricatedAuthorities`).
 */
export function CitationAuditBadge({ audit }: CitationAuditBadgeProps) {
  const hasUngrounded = (audit.ungroundedNumericClaims?.length ?? 0) > 0;
  const hasFabricated = (audit.fabricatedAuthorities?.length ?? 0) > 0;
  if (audit.sourceCount === 0 && !hasUngrounded && !hasFabricated) return null;

  const low = audit.isLowAdherence;
  const severe = hasFabricated; // İş Kolu BENCHMARK — worse than a missing citation: an INVENTED one.
  const Icon = severe ? AlertTriangle : low || hasUngrounded ? Link2Off : Link2;
  const titleParts: string[] = [];
  if (audit.sourceCount > 0) {
    titleParts.push(
      low
        ? `Zayıf kaynak kullanımı: ${audit.citedCount}/${audit.sourceCount} kaynak kullanıldı. Kullanılmayan: ${audit.missingSources.join(", ")}`
        : `${audit.citedCount}/${audit.sourceCount} kaynak kullanıldı: ${audit.citedSources.join(", ")}`,
    );
  }
  if (hasUngrounded) {
    // İş Kolu G3 — Grounded Verifier (numeric-claim edition): these
    // numbers appear in the turn but not in ANY source injected into
    // it — could be general knowledge OR an invented statistic; the
    // badge is a transparency signal, not an accusation.
    titleParts.push(
      `Kaynaklarda bulunamayan sayısal iddia(lar): ${audit.ungroundedNumericClaims!.join(", ")} — genel bilgi olabilir, kanıta dayalı olmayabilir.`,
    );
  }
  if (hasFabricated) {
    // İş Kolu BENCHMARK (2026-07 canlı bulgu) — konuşmacı, kendisine
    // HİÇ SAĞLANMAMIŞ isimli bir dış kurum/yayına (Gartner, HBR, ...)
    // atıfta bulundu. Bu, eksik atıftan daha ciddi bir sinyaldir.
    titleParts.push(
      `İcat edilmiş kaynak şüphesi: "${audit.fabricatedAuthorities!.join('", "')}" adlı kurum/yayın bu turda sağlanan hiçbir kaynakta yok.`,
    );
  }

  return (
    <span
      role="status"
      title={titleParts.join(" | ")}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider",
        severe
          ? "border-rose-500/60 bg-rose-500/15 text-rose-200"
          : low || hasUngrounded
            ? "border-amber-400/55 bg-amber-500/12 text-amber-200"
            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      <span className="normal-case font-normal">
        {audit.sourceCount > 0 ? `${audit.citedCount}/${audit.sourceCount} kaynak` : null}
        {audit.sourceCount > 0 && hasUngrounded ? " · " : null}
        {hasUngrounded ? `${audit.ungroundedNumericClaims!.length} doğrulanmamış sayı` : null}
        {(audit.sourceCount > 0 || hasUngrounded) && hasFabricated ? " · " : null}
        {hasFabricated ? `${audit.fabricatedAuthorities!.length} icat kaynak` : null}
      </span>
    </span>
  );
}
